import { Component, inject, Signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { environment } from '../../../../../environment/environment';
import { EntitySelectComponent } from '../../shared/components/entity-select/entity-select.component';
import { AuthService, DemoService } from '../../../../core/services';
import { Customer, CustomerLightData } from '../../../../core/models';
import { AuthStore, DemoStore, NavbarStore, SelectedCustomerStore } from '../../../../core/stores';
import * as Paths from '../../../../core/constants';
import { LanguageEnum, Role } from '../../../../core/enums';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  standalone: true,
  selector: 'dbx-customer-navbar',
  imports: [
    TranslateModule,
    MatMenuModule,
    MatIcon,
    RouterLink,
    MatToolbarModule,
    MatButton,
    EntitySelectComponent,
    MatTooltip,
  ],
  providers: [NavbarStore],
  templateUrl: 'customer-navbar.component.html',
  styleUrl: 'customer-navbar.component.scss',
})
export class CustomerNavbarComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly demoService = inject(DemoService);
  private readonly authStore = inject(AuthStore);
  private readonly demoStore = inject(DemoStore);
  private readonly selectedCustomerStore = inject(SelectedCustomerStore);
  private readonly store = inject(NavbarStore);

  authenticated = this.authStore.authenticated;
  $customers: Signal<CustomerLightData[]> = this.store.customers;
  $loading: Signal<boolean> = this.store.loading;
  $completed: Signal<boolean> = this.store.completed;
  $selectedCustomer: Signal<Customer | null> = this.selectedCustomerStore.selectedCustomer;
  $language = this.authStore.language;
  $user = this.authStore.user;
  $isDemo = this.demoStore.isDemoMode;

  customerDisplayFn = (c: CustomerLightData) => c?.companyName ?? '';

  readonly version = environment.version;
  readonly LanguageEnum = LanguageEnum;
  readonly Role = Role;
  readonly Paths = Paths;

  constructor() {
    if (this.authStore.authenticated() && !this.$user()?.customer) {
      this.loadCustomers();
    }
  }

  loadCustomers(searchText?: string) {
    this.store.loadCustomers(searchText);
  }

  loadMoreCustomers() {
    this.store.loadMoreCustomers();
  }

  selectCustomer(customer: CustomerLightData) {
    if (customer.id === this.selectedCustomerStore.selectedCustomer()?.id) {
      return;
    }
    this.selectedCustomerStore.setSelectedCustomer(customer as Customer);
    this.router.navigate([Paths.CYBERSECURITY_COCKPIT_PATH, Paths.DASHBOARD_PATH]);
  }

  handleLanguageChange(language: LanguageEnum) {
    this.store.changeCurrentUserLanguage(language);
  }

  toggleDemo() {
    if (this.$isDemo()) {
      this.demoService.deactivateDemo();
    } else {
      this.demoService.activateDemo();
    }
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => this.router.navigate([Paths.LOGIN_PATH]).then(),
      error: () => console.error('Something went wrong!'),
    });
  }

  protected readonly environment = environment;
}
